import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { purchaseOrderAPI, exportAPI } from '../lib/api';
import ExportButton from '../components/ExportButton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  FileText, Plus, CheckCircle2, Clock, Send, Eye, Edit2, Trash2, 
  Download, Loader2, Package, IndianRupee, XCircle, Building2
} from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  pending_approval: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  sent: 'bg-purple-100 text-purple-800',
  acknowledged: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800'
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  sent: 'Sent to Vendor',
  acknowledged: 'Acknowledged',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
  "Uttarakhand", "West Bengal", "Delhi", "Jammu and Kashmir", "Ladakh"
];

export default function PurchaseOrderPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pos, setPOs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(false);

  const isAdmin = ['admin', 'super_admin'].includes(user?.role);

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const [posRes, summaryRes] = await Promise.all([
        purchaseOrderAPI.getAll(params),
        purchaseOrderAPI.getSummary()
      ]);
      setPOs(posRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (po) => {
    try {
      const response = await purchaseOrderAPI.downloadPDF(po.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PO_${po.po_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleAction = async (poId, action) => {
    setActionLoading(true);
    try {
      switch (action) {
        case 'submit':
          await purchaseOrderAPI.submit(poId);
          toast.success('PO submitted for approval');
          break;
        case 'approve':
          await purchaseOrderAPI.approve(poId, { action: 'approve' });
          toast.success('PO approved');
          break;
        case 'reject':
          await purchaseOrderAPI.approve(poId, { action: 'reject', comments: 'Rejected by admin' });
          toast.success('PO rejected');
          break;
        case 'send':
          await purchaseOrderAPI.markSent(poId);
          toast.success('PO marked as sent');
          break;
        case 'acknowledge':
          await purchaseOrderAPI.acknowledge(poId);
          toast.success('PO acknowledged');
          break;
        case 'complete':
          await purchaseOrderAPI.complete(poId);
          toast.success('PO completed');
          break;
        default:
          break;
      }
      loadData();
      setShowViewDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (poId) => {
    if (!confirm('Are you sure you want to delete this PO?')) return;
    try {
      await purchaseOrderAPI.delete(poId);
      toast.success('PO deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // Create PO Dialog
  const CreatePODialog = () => {
    const [form, setForm] = useState({
      vendor: {
        name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: 'Madhya Pradesh',
        pincode: '',
        gstin: '',
        contact_person: '',
        email: '',
        phone: ''
      },
      line_items: [{ description: '', quantity: 1, unit: 'Nos', unit_price: 0, hsn_sac_code: '', gst_rate: 18, discount_percent: 0 }],
      payment_terms: 'Net 30',
      remarks: '',
      reference_number: ''
    });
    const [saving, setSaving] = useState(false);

    const updateVendor = (field, value) => {
      setForm(prev => ({
        ...prev,
        vendor: { ...prev.vendor, [field]: value }
      }));
    };

    const addLineItem = () => {
      setForm(prev => ({
        ...prev,
        line_items: [...prev.line_items, { description: '', quantity: 1, unit: 'Nos', unit_price: 0, hsn_sac_code: '', gst_rate: 18, discount_percent: 0 }]
      }));
    };

    const updateLineItem = (index, field, value) => {
      setForm(prev => ({
        ...prev,
        line_items: prev.line_items.map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }));
    };

    const removeLineItem = (index) => {
      if (form.line_items.length > 1) {
        setForm(prev => ({
          ...prev,
          line_items: prev.line_items.filter((_, i) => i !== index)
        }));
      }
    };

    const calculateTotal = () => {
      return form.line_items.reduce((sum, item) => {
        const lineTotal = item.quantity * item.unit_price;
        const discount = lineTotal * item.discount_percent / 100;
        const taxable = lineTotal - discount;
        const gst = taxable * item.gst_rate / 100;
        return sum + taxable + gst;
      }, 0);
    };

    const handleSubmit = async () => {
      if (!form.vendor.name.trim()) {
        toast.error('Please enter vendor name');
        return;
      }
      if (!form.vendor.address_line1.trim()) {
        toast.error('Please enter vendor address');
        return;
      }
      if (!form.vendor.city.trim()) {
        toast.error('Please enter vendor city');
        return;
      }
      if (!form.vendor.pincode.trim()) {
        toast.error('Please enter vendor pincode');
        return;
      }
      if (form.line_items.some(item => !item.description.trim() || item.quantity <= 0 || item.unit_price <= 0)) {
        toast.error('Please fill in all line items with description, quantity and price');
        return;
      }

      setSaving(true);
      try {
        await purchaseOrderAPI.create(form);
        toast.success('Purchase Order created');
        setShowCreateDialog(false);
        loadData();
      } catch (error) {
        console.error('PO creation error:', error.response?.data);
        const detail = error.response?.data?.detail;
        if (Array.isArray(detail)) {
          toast.error(detail.map(d => d.msg || d.type).join(', '));
        } else {
          toast.error(detail || 'Failed to create PO');
        }
      } finally {
        setSaving(false);
      }
    };

    return (
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Purchase Order</DialogTitle>
            <DialogDescription>Create a new purchase order for vendor</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Vendor Details */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Vendor Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vendor Name *</Label>
                  <Input
                    placeholder="Company name"
                    value={form.vendor.name}
                    onChange={(e) => updateVendor('name', e.target.value)}
                    data-testid="vendor-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Person</Label>
                  <Input
                    placeholder="Contact name"
                    value={form.vendor.contact_person}
                    onChange={(e) => updateVendor('contact_person', e.target.value)}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Address Line 1 *</Label>
                  <Input
                    placeholder="Street address"
                    value={form.vendor.address_line1}
                    onChange={(e) => updateVendor('address_line1', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input
                    placeholder="City"
                    value={form.vendor.city}
                    onChange={(e) => updateVendor('city', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>State *</Label>
                  <Select value={form.vendor.state} onValueChange={(v) => updateVendor('state', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INDIAN_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Pincode *</Label>
                  <Input
                    placeholder="Pincode"
                    value={form.vendor.pincode}
                    onChange={(e) => updateVendor('pincode', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>GSTIN</Label>
                  <Input
                    placeholder="GST Number"
                    value={form.vendor.gstin}
                    onChange={(e) => updateVendor('gstin', e.target.value.toUpperCase())}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="Email"
                    value={form.vendor.email}
                    onChange={(e) => updateVendor('email', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    placeholder="Phone"
                    value={form.vendor.phone}
                    onChange={(e) => updateVendor('phone', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <Package className="w-4 h-4" /> Line Items
                </h3>
                <Button variant="outline" size="sm" onClick={addLineItem}>
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>

              {form.line_items.map((item, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Item {index + 1}</span>
                    {form.line_items.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeLineItem(index)} className="h-6 w-6 p-0">
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    )}
                  </div>
                  <Input
                    placeholder="Item description"
                    value={item.description}
                    onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                  />
                  <div className="grid grid-cols-6 gap-2">
                    <div>
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Unit</Label>
                      <Select value={item.unit} onValueChange={(v) => updateLineItem(index, 'unit', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Nos">Nos</SelectItem>
                          <SelectItem value="Kg">Kg</SelectItem>
                          <SelectItem value="Ltr">Ltr</SelectItem>
                          <SelectItem value="Box">Box</SelectItem>
                          <SelectItem value="Set">Set</SelectItem>
                          <SelectItem value="Pcs">Pcs</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Rate (₹)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">HSN/SAC</Label>
                      <Input
                        placeholder="Code"
                        value={item.hsn_sac_code}
                        onChange={(e) => updateLineItem(index, 'hsn_sac_code', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">GST %</Label>
                      <Select value={String(item.gst_rate)} onValueChange={(v) => updateLineItem(index, 'gst_rate', parseFloat(v))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0%</SelectItem>
                          <SelectItem value="5">5%</SelectItem>
                          <SelectItem value="12">12%</SelectItem>
                          <SelectItem value="18">18%</SelectItem>
                          <SelectItem value="28">28%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Disc %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={item.discount_percent}
                        onChange={(e) => updateLineItem(index, 'discount_percent', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex justify-end">
                <div className="text-right">
                  <span className="text-sm text-muted-foreground">Estimated Total: </span>
                  <span className="text-lg font-bold">₹{calculateTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Terms</Label>
                <Select value={form.payment_terms} onValueChange={(v) => setForm(prev => ({ ...prev, payment_terms: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Advance">Advance Payment</SelectItem>
                    <SelectItem value="Net 15">Net 15 Days</SelectItem>
                    <SelectItem value="Net 30">Net 30 Days</SelectItem>
                    <SelectItem value="Net 45">Net 45 Days</SelectItem>
                    <SelectItem value="Net 60">Net 60 Days</SelectItem>
                    <SelectItem value="Net 90">Net 90 Days</SelectItem>
                    <SelectItem value="Net 180">Net 180 Days</SelectItem>
                    <SelectItem value="Net 365">Net 365 Days</SelectItem>
                    <SelectItem value="COD">Cash on Delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reference (Quotation/RFQ)</Label>
                <Input
                  placeholder="Reference number"
                  value={form.reference_number}
                  onChange={(e) => setForm(prev => ({ ...prev, reference_number: e.target.value }))}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Remarks / Terms & Conditions</Label>
                <Textarea
                  placeholder="Additional notes or terms..."
                  value={form.remarks}
                  onChange={(e) => setForm(prev => ({ ...prev, remarks: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="create-po-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              Create PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // View PO Dialog
  const ViewPODialog = () => {
    if (!selectedPO) return null;

    return (
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Purchase Order - {selectedPO.po_number}
            </DialogTitle>
            <Badge className={STATUS_COLORS[selectedPO.status]}>{STATUS_LABELS[selectedPO.status]}</Badge>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Vendor Info */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Vendor</CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  <p className="font-medium">{selectedPO.vendor?.name}</p>
                  <p className="text-muted-foreground">{selectedPO.vendor?.address_line1}</p>
                  <p className="text-muted-foreground">{selectedPO.vendor?.city}, {selectedPO.vendor?.state} {selectedPO.vendor?.pincode}</p>
                  {selectedPO.vendor?.gstin && <p className="mt-1">GSTIN: {selectedPO.vendor.gstin}</p>}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">PO Details</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                  <p><span className="text-muted-foreground">Date:</span> {new Date(selectedPO.created_at).toLocaleDateString()}</p>
                  <p><span className="text-muted-foreground">Payment:</span> {selectedPO.payment_terms}</p>
                  <p><span className="text-muted-foreground">Reference:</span> {selectedPO.reference_number || 'N/A'}</p>
                  <p><span className="text-muted-foreground">Created by:</span> {selectedPO.created_by_name}</p>
                </CardContent>
              </Card>
            </div>

            {/* Line Items */}
            <div>
              <h3 className="font-medium mb-2">Items</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>GST</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedPO.line_items?.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.quantity} {item.unit}</TableCell>
                      <TableCell>₹{item.unit_price?.toLocaleString('en-IN')}</TableCell>
                      <TableCell>{item.gst_rate}%</TableCell>
                      <TableCell className="text-right">₹{item.total_amount?.toLocaleString('en-IN')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>₹{selectedPO.subtotal?.toLocaleString('en-IN')}</span>
                </div>
                {selectedPO.total_discount > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Discount:</span>
                    <span>-₹{selectedPO.total_discount?.toLocaleString('en-IN')}</span>
                  </div>
                )}
                {selectedPO.cgst_amount > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>CGST:</span>
                    <span>₹{selectedPO.cgst_amount?.toLocaleString('en-IN')} (9%)</span>
                  </div>
                )}
                {selectedPO.sgst_amount > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>SGST:</span>
                    <span>₹{selectedPO.sgst_amount?.toLocaleString('en-IN')} (9%)</span>
                  </div>
                )}
                {selectedPO.igst_amount > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>IGST:</span>
                    <span>₹{selectedPO.igst_amount?.toLocaleString('en-IN')} (18%)</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Grand Total:</span>
                  <span>₹{selectedPO.grand_total?.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>

            {/* Remarks */}
            {selectedPO.remarks && (
              <div>
                <h3 className="font-medium mb-1">Remarks</h3>
                <p className="text-sm text-muted-foreground">{selectedPO.remarks}</p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => handleDownloadPDF(selectedPO)}>
              <Download className="w-4 h-4 mr-1" /> Download PDF
            </Button>
            
            {selectedPO.status === 'draft' && (
              <>
                <Button onClick={() => handleAction(selectedPO.id, 'submit')} disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                  Submit for Approval
                </Button>
              </>
            )}
            
            {selectedPO.status === 'pending_approval' && isAdmin && (
              <>
                <Button variant="destructive" onClick={() => handleAction(selectedPO.id, 'reject')} disabled={actionLoading}>
                  <XCircle className="w-4 h-4 mr-1" /> Reject
                </Button>
                <Button onClick={() => handleAction(selectedPO.id, 'approve')} disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                  Approve
                </Button>
              </>
            )}
            
            {selectedPO.status === 'approved' && (
              <Button onClick={() => handleAction(selectedPO.id, 'send')} disabled={actionLoading}>
                <Send className="w-4 h-4 mr-1" /> Mark as Sent
              </Button>
            )}
            
            {selectedPO.status === 'sent' && (
              <>
                <Button variant="outline" onClick={() => handleAction(selectedPO.id, 'acknowledge')} disabled={actionLoading}>
                  Vendor Acknowledged
                </Button>
                <Button onClick={() => handleAction(selectedPO.id, 'complete')} disabled={actionLoading}>
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Complete
                </Button>
              </>
            )}
            
            {selectedPO.status === 'acknowledged' && (
              <Button onClick={() => handleAction(selectedPO.id, 'complete')} disabled={actionLoading}>
                <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Complete
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="purchase-order-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope']">Purchase Orders</h1>
          <p className="text-muted-foreground">Manage purchase orders with official letterhead</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton 
            exportFn={exportAPI.purchaseOrders} 
            filename="purchase_orders.csv" 
            label="Export"
          />
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-po-btn">
            <Plus className="w-4 h-4 mr-2" /> New Purchase Order
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid gap-4 md:grid-cols-5">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{summary.total_pos}</div>
              <p className="text-sm text-muted-foreground">Total POs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">{summary.pending_approval_count}</div>
              <p className="text-sm text-muted-foreground">Pending Approval</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">{summary.approved_count}</div>
              <p className="text-sm text-muted-foreground">Approved</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-emerald-600">{summary.completed_count}</div>
              <p className="text-sm text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">₹{(summary.total_value / 100000).toFixed(1)}L</div>
              <p className="text-sm text-muted-foreground">Total Value</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter Tabs */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="draft">Draft</TabsTrigger>
          <TabsTrigger value="pending_approval">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* PO List */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>PO Number</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No purchase orders found
                  </TableCell>
                </TableRow>
              ) : (
                pos.map((po) => (
                  <TableRow key={po.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { setSelectedPO(po); setShowViewDialog(true); }}>
                    <TableCell className="font-medium">{po.po_number}</TableCell>
                    <TableCell>{po.vendor?.name}</TableCell>
                    <TableCell>{new Date(po.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>₹{po.grand_total?.toLocaleString('en-IN')}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[po.status]}>{STATUS_LABELS[po.status]}</Badge>
                    </TableCell>
                    <TableCell>{po.created_by_name}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedPO(po); setShowViewDialog(true); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(po)}>
                          <Download className="w-4 h-4" />
                        </Button>
                        {po.status === 'draft' && isAdmin && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(po.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <CreatePODialog />
      <ViewPODialog />
    </div>
  );
}
